import { useStyles } from "./styles";
import ClearIcon from "@material-ui/icons/HighlightOff";
import { Box, Button, Grid, IconButton, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useEffect, useState } from "react";

const Medicines = ({
  medicines,
  type,
  addNewMedicine,
  updateMedicines,
  removeMedicine,
  allMedicines,
}) => {
  const classes = useStyles();
  const [searchQueries, setSearchQueries] = useState({});

  useEffect(() => {
    ValidatorForm.addValidationRule("isValidMedicineName", (value) => {
      if (!value.trim()) return true;
      return /^[a-zA-Z0-9\s\-.,()/]+$/.test(value) && value.length <= 100;
    });

    ValidatorForm.addValidationRule("isValidDosage", (value) => {
      if (!value.trim()) return true;
      return /^[a-zA-Z0-9\s\-.,()/mgML]+$/.test(value) && value.length <= 50;
    });

    ValidatorForm.addValidationRule("isValidFrequency", (value) => {
      if (!value.trim()) return true;
      return /^[a-zA-Z0-9\s\-.,()/times]+$/.test(value) && value.length <= 50;
    });

    ValidatorForm.addValidationRule("isValidInstruction", (value) => {
      if (value && value.trim()) {
        return value.length <= 350;
      }
      return true;
    });
    ValidatorForm.addValidationRule(
      "medicineRequiredIfOtherFields",
      (value, allValues) => {
        return true;
      }
    );

    return () => {
      ValidatorForm.removeValidationRule("isValidMedicineName");
      ValidatorForm.removeValidationRule("isValidDosage");
      ValidatorForm.removeValidationRule("isValidFrequency");
      ValidatorForm.removeValidationRule("isValidInstruction");
      ValidatorForm.removeValidationRule("medicineRequiredIfOtherFields");
    };
  }, []);

  const handleMedicineChange = (index, field, value) => {
    const updatedMedicines = [...medicines];
    updatedMedicines[index][field] = value;

    if (field === "medicineName" || field === "dosage") {
      updatedMedicines[index].isSelected = false;
    }

    updateMedicines(type, updatedMedicines);
  };

  const handleMedicineSelect = (index, selectedMedicine) => {
    if (selectedMedicine) {
      const updatedMedicines = [...medicines];
      updatedMedicines[index] = {
        ...updatedMedicines[index],
        medicineName:
          selectedMedicine.medicineName || selectedMedicine.name || "",
        dosage: selectedMedicine.dosage || "",
        frequency: selectedMedicine.frequency || "",
        instruction: selectedMedicine.instruction || "",
        isSelected: true,
      };
      updateMedicines(type, updatedMedicines);
    }
  };

  const addMedicine = () => {
    if (medicines.length > 0) {
      const lastMedicine = medicines[medicines.length - 1];
      const emptyFields = [];
      if (!lastMedicine.medicineName.trim()) emptyFields.push("Medicine Name");
      if (!lastMedicine.dosage.trim()) emptyFields.push("Dosage");
      if (!lastMedicine.frequency.trim()) emptyFields.push("Frequency");
      if (!(lastMedicine.duration || "").trim()) emptyFields.push("Duration");
      if (!(lastMedicine.medicineTime || "").trim()) emptyFields.push("Timing");

      if (emptyFields.length > 0) {
        alert(
          `Please complete these fields before adding another medicine: ${emptyFields.join(
            ", "
          )}`
        );
        return;
      }
    }
    addNewMedicine(type);
  };

  const getFilteredMedicines = (index) => {
    const query = searchQueries[index] || "";
    if (!query.trim()) {
      return allMedicines;
    }
    return allMedicines.filter((med) => {
      const medicineName = (med.medicineName || med.name || "").toLowerCase();
      return medicineName.includes(query.toLowerCase());
    });
  };

  return (
    <Box sx={{ marginTop: 16 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="nowrap"
      >
        <Typography variant="h6">Medicines</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={addMedicine}
          className={classes.addMedicineButton}
        >
          Add Medicine
        </Button>
      </Box>

      {medicines.map((medicine, index) => {
        const filteredMedicines = getFilteredMedicines(index);

        return (
          <Box key={`${type}-${index}`} display="flex" sx={{ marginTop: 16 }}>
            <Grid
              container
              spacing={2}
              className={classes.medicineDetailsContainer}
            >
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={filteredMedicines}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option.medicineName || option.name || ""
                  }
                  value={
                    filteredMedicines.find(
                      (opt) =>
                        opt.medicineName === medicine.medicineName ||
                        opt.name === medicine.medicineName
                    ) || medicine.medicineName
                  }
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      handleMedicineChange(index, "medicineName", newValue);
                    } else if (newValue) {
                      handleMedicineSelect(index, newValue);
                    }
                  }}
                  inputValue={medicine.medicineName}
                  onInputChange={(event, newInputValue) => {
                    handleMedicineChange(index, "medicineName", newInputValue);
                    setSearchQueries((prev) => ({
                      ...prev,
                      [index]: newInputValue,
                    }));
                  }}
                  noOptionsText={
                    searchQueries[index]?.trim()
                      ? "No medicines found"
                      : "No data available"
                  }
                  renderInput={(params) => (
                    <TextValidator
                      {...params}
                      label="Medicine Name"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={medicine.medicineName}
                      onChange={(e) =>
                        handleMedicineChange(
                          index,
                          "medicineName",
                          e.target.value
                        )
                      }
                      validators={[
                        "isValidMedicineName",
                        ...(medicine.dosage || medicine.frequency
                          ? ["required"]
                          : []),
                      ]}
                      errorMessages={[
                        "Use only letters, numbers and common symbols (max 100 characters)",
                        "Medicine Name is required if dosage or frequency is filled",
                      ]}
                    />
                  )}
                />
              </Grid>

              {/* Dosage */}
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <TextValidator
                  label="Dosage"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={medicine.dosage}
                  onChange={(e) =>
                    handleMedicineChange(index, "dosage", e.target.value)
                  }
                  validators={[
                    ...(medicine.medicineName ? ["required"] : []),
                    "isValidDosage",
                  ]}
                  errorMessages={[
                    "Dosage is required when medicine name is entered",
                    "Include numbers and units like mg or mL (max 50 characters)",
                  ]}
                />
              </Grid>

              {/* Frequency */}
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <TextValidator
                  label="Frequency"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={medicine.frequency}
                  onChange={(e) =>
                    handleMedicineChange(index, "frequency", e.target.value)
                  }
                  validators={[
                    ...(medicine.medicineName ? ["required"] : []),
                    "isValidFrequency",
                  ]}
                  errorMessages={[
                    "Frequency is required when medicine name is entered",
                    "Examples: '2 times daily' or 'every 6 hours' (max 50 characters)",
                  ]}
                />
              </Grid>

              {/* Duration */}
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <TextValidator
                  label="Duration"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={medicine.duration || ""}
                  onChange={(e) =>
                    handleMedicineChange(index, "duration", e.target.value)
                  }
                  validators={medicine.medicineName ? ["required"] : []}
                  errorMessages={[
                    "Duration is required when medicine name is entered",
                  ]}
                  placeholder="E.g., 15 days"
                />
              </Grid>

              {/* Timing */}
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <TextValidator
                  label="Timing"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={medicine.medicineTime || ""}
                  onChange={(e) =>
                    handleMedicineChange(index, "medicineTime", e.target.value)
                  }
                  validators={medicine.medicineName ? ["required"] : []}
                  errorMessages={[
                    "Timing is required when medicine name is entered",
                  ]}
                  placeholder="E.g., After meals"
                />
              </Grid>

              {/* Instruction */}
              <Grid item xs={12}>
                <TextValidator
                  label="Special Instructions"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={medicine.instruction}
                  onChange={(e) =>
                    handleMedicineChange(index, "instruction", e.target.value)
                  }
                  validators={["isValidInstruction"]}
                  errorMessages={[
                    "Instructions should be less than 350 characters",
                  ]}
                  multiline
                  rows={2}
                  placeholder="E.g., Take before meals with warm water"
                />
              </Grid>
            </Grid>

            {/* Remove button */}
            <Box sx={{ width: 80 }}>
              {medicines.length > 1 && (
                <Grid item xs={12} sm={6} md={1} lg={1} xl={1}>
                  <IconButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => removeMedicine(index)}
                    aria-label="Remove medicine"
                  >
                    <ClearIcon />
                  </IconButton>
                </Grid>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default Medicines;
